import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Eraser, Check } from 'lucide-react';

const SignaturePad = ({ onSave, onClear, width = 400, height = 150 }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Set up canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Draw signature line
        ctx.beginPath();
        ctx.strokeStyle = '#e5e5e5';
        ctx.moveTo(20, height - 30);
        ctx.lineTo(width - 20, height - 30);
        ctx.stroke();
        ctx.strokeStyle = '#1a1a1a';
    }, [width, height]);

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        
        if (e.touches) {
            return {
                x: (e.touches[0].clientX - rect.left) * scaleX,
                y: (e.touches[0].clientY - rect.top) * scaleY
            };
        }
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasSignature(true);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Redraw signature line
        ctx.beginPath();
        ctx.strokeStyle = '#e5e5e5';
        ctx.moveTo(20, height - 30);
        ctx.lineTo(width - 20, height - 30);
        ctx.stroke();
        ctx.strokeStyle = '#1a1a1a';
        
        setHasSignature(false);
        if (onClear) onClear();
    };

    const saveSignature = () => {
        if (!hasSignature) return;
        const canvas = canvasRef.current;
        const dataUrl = canvas.toDataURL('image/png');
        if (onSave) onSave(dataUrl);
    };

    return (
        <div className="space-y-3">
            <div className="border rounded-lg overflow-hidden bg-white">
                <canvas
                    ref={canvasRef}
                    width={width}
                    height={height}
                    className="w-full touch-none cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                />
            </div>
            <p className="text-xs text-muted-foreground text-center">
                Bitte unterschreiben Sie oben mit der Maus oder dem Finger
            </p>
            <div className="flex justify-between">
                <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearSignature}
                    type="button"
                >
                    <Eraser className="w-4 h-4 mr-2" />
                    Löschen
                </Button>
                <Button 
                    size="sm" 
                    onClick={saveSignature}
                    disabled={!hasSignature}
                    type="button"
                >
                    <Check className="w-4 h-4 mr-2" />
                    Unterschrift bestätigen
                </Button>
            </div>
        </div>
    );
};

export default SignaturePad;
